import { Tooltip } from "antd";
import { ReactNode } from "react";
import { formatDateTime } from "../../utils/date_formatter";
import AuditLogButton from "../AuditLogButton";
import ReportHistoryTooltip from "../Tooltip/ReportHistoryTooltip";

type ReportCreatedLineProps = {
	// When the report was submitted for review; until then the line says who
	// created it.
	submittedOn?: string;
	authorName?: string;
	authorEmail?: string | null;
	// An anonymous report names no author, not even on hover.
	isAnonymous?: boolean;
	// Heads the review history popover, such as "Audit review history".
	historyTitle: string;
	// The report, with its people's names, for the review history popover.
	historyData: any;
	onOpenAuditLog: () => void;
};

// The line under the title in a report's header: when it was submitted and by
// whom, and its review history on hover.
function ReportCreatedLine(props: Readonly<ReportCreatedLineProps>) {
	const {
		submittedOn,
		authorName,
		authorEmail,
		isAnonymous,
		historyTitle,
		historyData,
		onOpenAuditLog,
	} = props;

	// Only text is shown: nothing checks what else could be stored there.
	const email = typeof authorEmail === "string" ? authorEmail : undefined;
	const name = typeof authorName === "string" ? authorName : undefined;

	let author: ReactNode = email;
	if (isAnonymous) {
		author = "Anonymous";
	} else if (name) {
		author = (
			<Tooltip title={email}>
				<span>{name}</span>
			</Tooltip>
		);
	}

	return (
		<>
			<span>
				{/* When it was submitted for review; the items' stages show when
				    its draft was first saved, as Created. */}
				{submittedOn
					? `Submitted ${formatDateTime(submittedOn)} by `
					: "Created by "}
				{author}
			</span>
			{/* Opens on hover only, so the trigger isn't a button: it promises no
			    click or keyboard action. */}
			<ReportHistoryTooltip
				title={
					<div className="flex justify-between items-center gap-5">
						<div className="text-base font-medium">{historyTitle}</div>
						<AuditLogButton
							moduleKey="reporting"
							handleClick={onOpenAuditLog}
						/>
					</div>
				}
				data={historyData}
			>
				<span className="report-header__hint">Review history</span>
			</ReportHistoryTooltip>
		</>
	);
}

export default ReportCreatedLine;
